/* SVERMEN — the school, drawn on the water.
 *
 * WHY THIS IS NOT WINDY, AND MUST NOT PRETEND TO BE.
 *
 * The obvious version of this is Windy's particle field: thousands of motes
 * advected along a vector grid, where the motion IS the data. We cannot build
 * that one honestly. There is no hydrodynamic grid available to this product —
 * `api.phoniq.no` publishes cohorts, lice and settefisk and nothing about
 * water — so particles drifting across a fjord here would be an animation of a
 * current nobody measured, drawn over a real satellite image of a real farm at
 * a real hour. That is the most expensive kind of wrong: it looks like
 * evidence.
 *
 * SINCE 2026-08-30 THERE IS A CURRENT, AND THIS FILE STILL REFUSES IT.
 * `/v1/sites/:id/flow` serves an hourly forecast, so the sentence above is no
 * longer literally true — the premise changed and the paragraph is kept rather
 * than quietly deleted, because the conclusion did not change with it.
 *
 * The forecast's grid is about a kilometre across and an array is half of one,
 * so it resolves ONE vector per farm. That is enough to say which side of a
 * cage the water arrives on, which is what `Anleggsvisning` now draws: two
 * arrows per ring, static, on a fixed scale, labelled as modelled. It is not
 * remotely enough to advect a particle field. Motes drifting BETWEEN the rings
 * would be drawing structure at ten metres out of a model that has none below a
 * kilometre — the same lie as before, now with a citation, which is worse
 * rather than better.
 *
 * So the particles are still not water. They are the FISH, and that is a claim we
 * can actually stand behind: salmon in a sea cage swim as a rotating school, a
 * slow torus around the centre of the ring. It is what you see looking down
 * into a pen from the walkway, it is what the crew already pictures when they
 * think about a merd, and it means the motion depicts something that is
 * genuinely happening in the water underneath that pixel.
 *
 * WHAT THE MOTION IS ALLOWED TO SAY.
 *
 *   an empty ring has NO PARTICLES.  Brakklegging becomes stillness, and a
 *      fallow ring is legible from across a feed barge without reading a word.
 *      This is the one that earns the whole feature: emptiness has been a
 *      colour until now, and colour is what the eye stops noticing.
 *
 *   density is per SQUARE METRE, not per ring. A 160-ring holds more fish than
 *      a 120-ring and shows more. The array's real proportions survive.
 *
 *   colour is the band, and only where there is a count to colour by. A ring
 *      nobody has counted swims in the neutral, matching its dashed outline:
 *      movement without a claim.
 *
 * WHAT IT IS NOT ALLOWED TO SAY. Speed and direction encode NOTHING. There is
 * no per-pen swimming-speed measurement, and the current forecast is per FARM
 * and not per pen, so making the school spin faster where the lice are worse —
 * or turning it to face the forecast — would be inventing a reading — the
 * exact failure the whole file is written to avoid. The arrows carry the
 * current; the fish carry the fish, and the two must not be joined up because
 * they happen to share a ring. Density does not track
 * lice either: more particles reads as MORE FISH, not as more lice, and a
 * viewer's first instinct is the one that wins.
 *
 * `docs/principles.md` asks Function / Feeling / Decoration of anything that
 * moves. Emptiness-as-stillness is Function. A farm that looks alive the first
 * time somebody opens it is Feeling, which the test allows. Neither is the
 * third thing.
 *
 * WHAT WAS WRONG WITH THE FIRST VERSION, AND IT IS WORTH WRITING DOWN.
 *
 * Every mote ran a PERFECT CIRCLE AT A CONSTANT ANGULAR SPEED, forever. The
 * only thing that ever varied was a three-per-cent radial wobble. That is a
 * turntable with confetti on it, and no amount of tuning the speed fixes it,
 * because the problem is not the rate — it is that nothing in the picture ever
 * makes a DECISION. A viewer reads "spinning" in about a second and then stops
 * looking, which is the opposite of what a live farm on a wall is for.
 *
 * Five things separate a school from a carousel, and none of them is speed:
 *
 *   THEY ARE ORIENTED. A dot has no heading. A fish is longer than it is wide
 *      and points where it is going, so the eye reads direction before it
 *      reads motion. This is the single biggest change in the file.
 *
 *   THEY BURST AND GLIDE. Salmon do not cruise at a fixed speed; they beat the
 *      tail, shoot forward, and coast. Every mote carries its own beat, with
 *      its own frequency and phase, so the school shimmers instead of sliding.
 *
 *   THEY CHANGE LANES. `rf` used to be fixed at birth, which built a set of
 *      nested rings that could never reorganise. Now each fish drifts toward a
 *      new radius every few seconds, and because the inner lanes turn faster,
 *      fish overtake each other and the pattern never repeats.
 *
 *   THEY BUNCH. A real school has arcs of density that travel around the ring.
 *      A slowly rotating speed field does this for free: fish pile up where
 *      the field is slow and string out where it is fast.
 *
 *   THEY ARE STARTLED. Every twenty seconds or so — never on a round number,
 *      never in step with any other pen — the school tightens and surges, then
 *      settles. This is the one that makes somebody glance back at the screen.
 *      Nothing on the page is animated in a loop the eye can learn.
 *
 * And depth. A fish two metres down is smaller and dimmer than one at the
 * surface, and fish rise and sink constantly. This is the one place in the
 * product where alpha is right rather than forbidden: `not-generic.md` bans it
 * on SURFACES, where a translucent panel is a lazy substitute for a chosen
 * colour. Here it is not a surface — it is a picture of water, and water is
 * the thing that is actually between the viewer and the fish.
 *
 * NONE OF IT ENCODES ANYTHING, and that is load-bearing. Speed, beat, bunching
 * and startle are all pseudo-random and per-pen, and the startle schedules are
 * deliberately NOT shared between rings, because seven schools flinching at
 * the same instant would read as a common cause — a boat, a seal, a feeding —
 * and this product has no idea whether one happened. Randomness says nothing.
 * Synchrony would be a claim.
 */
import { offset } from './merdgeo'

/** One ring, as much of it as the school needs to know. */
export interface Sverm {
  penId: string
  lon: number
  lat: number
  radiusM: number
  /** 1, 2 or 3 from `antallFor`, or 0 where there is no figure to band. */
  band: number
  /** No fish in this ring. Nothing swims; the water is still. */
  tom: boolean
  /** Fish, but nobody has counted them. Neutral, like the dashed outline. */
  utelt: boolean
}

/** Fish per square metre of surface, at the top of the cage.
 *
 *  Not a stocking density — this is a picture, not a biomass model, and a real
 *  160-ring holds tens of thousands of salmon which would render as a solid
 *  disc. Tuned so a 160-ring carries roughly forty motes: enough to read as a
 *  school rather than as a handful of dots, few enough that seven of them cost
 *  nothing. */
const PER_M2 = 0.00006

/** Radians per second, before any of the variation below. A school this size
 *  turns slowly; anything quicker reads as a stirred liquid rather than as
 *  fish. */
const OMEGA = 0.2

/** Below this the ring is a few pixels across and a school inside it is mud.
 *  Zoomed out, the array should read as rings, which it already does. */
const MIN_PX = 26

const TAU = Math.PI * 2
const MAX_PER_PEN = 90

interface Mote {
  a: number      // where it is on the ring
  rf: number     // how far out, now
  rm: number     // how far out it is heading
  rt: number     // seconds until it picks somewhere else
  wb: number     // its own share of the school's speed
  fh: number     // tail beats per second
  fs: number     // where in its own beat it is
  st: number     // body size; a school is not a set of identical fish
  dp: number     // depth, 0 at the surface
  dm: number     // depth it is drifting toward
  dt: number     // seconds until it picks another
}

/** One ring's school, and the state that belongs to the school rather than to
 *  any fish in it. */
interface Stim {
  m: Mote[]
  t: number          // this school's own clock, started at a random offset
  puls: number       // how startled it is right now, 1 down to 0
  neste: number      // seconds until the next startle
  d1: number         // two phases for the slow wander of the whole school
  d2: number
}

/** sqrt keeps the ring evenly covered. Drawing rf flat piles every mote into
 *  the middle, because area grows with the square of the radius, and a school
 *  with a dense core and an empty rim is the opposite of the real thing — a
 *  salmon torus is hollow. */
const radiusBrøk = () => Math.sqrt(0.15 + Math.random() * 0.8)

/** Never a round number and never the same twice: a startle you can predict is
 *  a loop, and a loop is the thing the eye stops watching. */
const tilNesteSkvett = () => 11 + Math.random() * 26

function nyStim(n: number): Stim {
  const m: Mote[] = []
  for (let i = 0; i < n; i++) {
    const rf = radiusBrøk()
    m.push({
      a: Math.random() * TAU,
      rf,
      rm: radiusBrøk(),
      rt: 2 + Math.random() * 9,
      /* Inner fish come round faster. A rigid disc looks like a turntable; the
         shear is what makes it look alive. */
      wb: (1.3 - 0.4 * rf) * (0.82 + Math.random() * 0.36),
      fh: 1.1 + Math.random() * 1.5,
      fs: Math.random() * TAU,
      st: 0.78 + Math.random() * 0.5,
      dp: Math.random(),
      dm: Math.random(),
      dt: 3 + Math.random() * 8,
    })
  }
  return {
    m,
    t: Math.random() * 500,
    puls: 0,
    neste: tilNesteSkvett(),
    d1: Math.random() * TAU,
    d2: Math.random() * TAU,
  }
}

function farger(): Record<string, string> {
  const s = getComputedStyle(document.documentElement)
  const p = (n: string, fb: string) => s.getPropertyValue(n).trim() || fb
  return {
    /* The band ladder, read off the same tokens the rings are painted from so
       a retheme cannot leave the school behind. */
    1: p('--signal', '#b4d9c7'),
    2: p('--warn', '#e9c46a'),
    3: p('--pulse', '#e63926'),
    /* --hairline-strong, which is the token this repo actually defines. It
       used to ask for --line-strong, which it does not, so the school has been
       swimming on the hard-coded fallback and a retheme would have left it
       behind — the exact failure the comment above warns about. */
    0: p('--hairline-strong', '#6b8d7e'),
  }
}

/** Start the school. Returns a function that stops it and clears the canvas.
 *
 *  `hent` is called once per frame rather than closed over, because the rings
 *  are recoloured by props on every count and a school still swimming last
 *  week's colours is worse than no school.
 */
export function startSverm(
  canvas: HTMLCanvasElement,
  map: any,
  hent: () => Sverm[],
): () => void {
  const stille = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}

  const flokk = new Map<string, Stim>()
  const farge = farger()
  let ram = 0
  let forrige = 0
  let levende = true

  const mål = () => {
    const r = canvas.getBoundingClientRect()
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const w = Math.max(1, Math.round(r.width * dpr))
    const h = Math.max(1, Math.round(r.height * dpr))
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }
    return dpr
  }

  const tegn = (nå: number) => {
    if (!levende) return
    const dt = forrige ? Math.min(0.05, (nå - forrige) / 1000) : 0
    forrige = nå

    const dpr = mål()
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr)

    for (const s of hent()) {
      // AN EMPTY RING IS STILL. Nothing to draw, and the nothing is the point.
      if (s.tom) { flokk.delete(s.penId); continue }

      let c
      try { c = map.project([s.lon, s.lat]) } catch { continue }
      const kant = offset(s.lat, s.lon, s.radiusM, 0)
      let e
      try { e = map.project(kant) } catch { continue }
      const R = Math.hypot(e.x - c.x, e.y - c.y)
      if (R < MIN_PX) { continue }

      const areal = Math.PI * s.radiusM * s.radiusM
      const n = Math.max(6, Math.min(MAX_PER_PEN, Math.round(areal * PER_M2 * 1000)))
      let st = flokk.get(s.penId)
      if (!st || st.m.length !== n) { st = nyStim(n); flokk.set(s.penId, st) }
      st.t += dt

      /* THE STARTLE. It decays on a time constant rather than a frame count,
         so it is the same event on a 60Hz laptop and a 120Hz phone. */
      st.neste -= dt
      if (st.neste <= 0) { st.puls = 1; st.neste = tilNesteSkvett() }
      if (st.puls > 0) st.puls = Math.max(0, st.puls - dt / 1.7)
      const skvett = st.puls * st.puls          // sharp onset, long tail

      /* THE SCHOOL'S OWN SURGE. Two periods that do not divide into each
         other, so the sum never repeats inside anyone's attention span. */
      const surge = 1
        + 0.2 * Math.sin(st.t * 0.107 + st.d1)
        + 0.11 * Math.sin(st.t * 0.283 + st.d2)
        + 1.05 * skvett

      /* AND ITS WANDER. A real school is not centred on the ring — it hangs
         off to one side and drifts. Eight per cent of the radius is enough to
         stop the whole thing looking pinned to a peg. */
      const ox = Math.sin(st.t * 0.061 + st.d1) * R * 0.08
      const oy = Math.cos(st.t * 0.049 + st.d2) * R * 0.08

      const grunn = s.utelt ? farge[0]! : (farge[s.band] ?? farge[0]!)
      ctx.fillStyle = grunn
      /* One pixel at a distance, two up close. A body that keeps its size
         through the zoom stops being a fish and becomes a screen artefact. */
      const rad = R > 90 ? 1.75 : 1.15

      for (const p of st.m) {
        if (dt) {
          /* Lane changes. Ease toward the target rather than jumping, and pick
             a new one when the clock runs out. Under a startle the whole
             school pulls in toward the middle, which is what tightening
             actually is. */
          p.rt -= dt
          if (p.rt <= 0) { p.rm = radiusBrøk(); p.rt = 3 + Math.random() * 9 }
          p.rf += (p.rm * (1 - 0.22 * skvett) - p.rf) * Math.min(1, dt * 0.55)

          p.dt -= dt
          if (p.dt <= 0) { p.dm = Math.random(); p.dt = 3 + Math.random() * 8 }
          p.dp += (p.dm - p.dp) * Math.min(1, dt * 0.3)

          p.fs += p.fh * TAU * dt
          /* Burst and glide, not a sine. The push is short and hard, the coast
             is long and easy, which is how a tail actually works. */
          const sn = Math.sin(p.fs)
          const slag = 1 + (sn > 0 ? 0.5 * sn * sn : -0.3 * sn * sn)

          /* The density wave: a slow field around the ring that fish are quick
             through and slow through, so arcs of them bunch and string out.
             Turning at its own rate, against the school. */
          const felt = 1 + 0.17 * Math.sin(p.a * 2 - st.t * 0.13)

          p.a += OMEGA * p.wb * surge * slag * felt * dt
        }

        const rr = p.rf * R
        const x = c.x + ox + Math.cos(p.a) * rr
        const y = c.y + oy + Math.sin(p.a) * rr

        /* Depth: smaller and dimmer further down, and it is the alpha that
           sells it — fish surfacing and sinking out of view is most of what
           looking into a pen actually looks like. */
        const krymp = 1 - 0.3 * p.dp
        const lang = rad * 2.5 * p.st * krymp
        const brei = rad * 0.82 * p.st * krymp

        ctx.globalAlpha = 1 - 0.45 * p.dp
        ctx.beginPath()
        /* Rotated to the tangent — the direction it is travelling — which is
           what turns a dot into something with a head and a tail. */
        ctx.ellipse(x, y, lang, brei, p.a + Math.PI / 2, 0, TAU)
        ctx.fill()
      }
      ctx.globalAlpha = 1
    }

    if (!stille) ram = requestAnimationFrame(tegn)
  }

  /* REDUCED MOTION KEEPS THE SCHOOL AND DROPS THE SWIMMING. A still scatter
     inside a ring still says "there are fish in this one" and an empty ring
     still says nothing, which is the fact the feature exists for. Removing the
     particles entirely would take the information away with the animation.
     They are still drawn as oriented bodies: a stopped fish is still a fish. */
  if (stille) {
    const om = () => { forrige = 0; tegn(0) }
    map.on('move', om)
    map.on('resize', om)
    om()
    return () => { levende = false; map.off('move', om); map.off('resize', om) }
  }

  const synlig = () => {
    if (document.hidden) { cancelAnimationFrame(ram); ram = 0 }
    else if (!ram) { forrige = 0; ram = requestAnimationFrame(tegn) }
  }
  document.addEventListener('visibilitychange', synlig)
  ram = requestAnimationFrame(tegn)

  return () => {
    levende = false
    cancelAnimationFrame(ram)
    document.removeEventListener('visibilitychange', synlig)
  }
}
